// @ts-expect-error TS6133 -- MIGRATION: not approved, fix when encountered
import React, { memo, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  ErrorAlert,
  PageLoadingSpinner,
  PrescriptionPopUp,
  useAdjustPageHeadEntities,
  useUpdateEffect,
} from 'ui_kit_qmd'
import {
  DELETE_NOTE_ARIA_LABEL_TEXT,
  DELETE_PATIENT_INSTRUCTION_ARIA_LABEL_TEXT,
  LOCAL_STORAGE_STATE_TO_PAST_VISIT,
  NO_NOTES_TEXT,
  NO_PATIENT_INSTRUCTIONS_TEXT,
  PATIENT_CHART_PAGE_ROUTE,
  PATIENT_CHARTS_PAGE_ROUTE,
  PATIENT_CHARTS_TEXT,
  PATIENT_INSTRUCTIONS_TITLE_TEXT,
  QUICK_MD_TITLE_TEXT,
  VISIBLE_TO_PATIENT_IN_VISIT_SUMMARY_TEXT,
  VISIT_NOTE_BLOCK_NOTE_SECTION_TITLE,
  VISIT_NOTE_TEXT,
} from '../../utils/constants'
import { useCanDeleteMedicalRecords } from '@/hooks/useCanDeleteMedicalRecords'
import {
  DeleteMedicalRecordModal,
  type DeleteMedicalRecordTarget,
} from './components/delete-medical-record-modal/DeleteMedicalRecordModal'
import { ICDCodesSection } from './components/icd-codes-section/ICDCodesSection'
import { NotesSection } from './components/notes-section/NotesSection'
import { PastVisitInfoHeader } from './components/past-visit-info-header/PastVisitInfoHeader'
import { PatientInfoCard } from './components/patient-info-card/PatientInfoCard'
import { PrescriptionsSection } from './components/prescriptions-section/PrescriptionsSection'
import type { LegacyPrescription } from '../../utils/prescriptionMapper'

import './visitInfo.scss'
import { PastVisitListener } from '../../store/pastVisit/PastVisitListener'
import errorIcon from '../../images/common/error-icon.svg'
import { setError } from '../../store/pastVisit/PastVisitActions'
import { getVisitStatus, parseTimeZone } from '../../utils/common'
import { getPatientInfo, getUpcomingVisitRemarks } from '../../store/charts/ChartsThunk'
import { generateLoggVisitsAccessData, postHIPAALogMessage } from '../../utils/HippaLoggingHelper'
import { formatNameForPatientChartTitle } from '../patient-chart-page/helper'
import { getProviderInfo, getVisitInfo } from '../../store/pastVisit/PastVisitThunk'
import AddVisitRemarksSection from './components/add-visit-remarks-section/AddVisitRemarksSection'

const errorToastContainerStyle = {
  width: 550,
  height: 56,
  background: '#fdf5f2',
  zIndex: 10,
}

// @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
export const VisitInfo = memo(({ isUpcomingVisit }) => {
  const { id } = useParams()
  const { state: locationState } = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  // @ts-expect-error TS2769 -- MIGRATION: not approved, fix when encountered
  const stateLocation = locationState ?? JSON.parse(localStorage.getItem(LOCAL_STORAGE_STATE_TO_PAST_VISIT))?.[id]
  const visitId = stateLocation?.visitId ?? id

  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const visitInfo = useSelector((state) => state.pastVisitReducer.pastVisitsInfo?.[visitId]?.visitInfo || {})

  const patientId = stateLocation?.patientId ?? visitInfo?.PatientId

  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const profile = useSelector((state) => state.accountSettings.adminProfile)

  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const error = useSelector((state) => state.pastVisitReducer.pastVisitsInfo?.[visitId]?.error || '')
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const loading = useSelector((state) => state.pastVisitReducer.pastVisitsInfo?.[visitId]?.loading)
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const providerData = useSelector((state) => state.pastVisitReducer.pastVisitsInfo?.[visitId]?.providerData || {})
  const provider = Object.keys(stateLocation?.provider || {}).length ? stateLocation?.provider : providerData
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const patientInfo = useSelector((state) => state.chartsReducer.patientInfo)?.[patientId ?? visitInfo?.PatientId] || {}

  const [userTimeZone, setUserTimeZone] = useState(parseTimeZone(profile.TimeZone))
  const [prescription, setPrescription] = useState<LegacyPrescription | null>(null)
  // The target outlives the dialog's open flag so its copy doesn't change while it animates closed.
  const [deleteTarget, setDeleteTarget] = useState<DeleteMedicalRecordTarget | null>(null)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)

  const canDeleteMedicalRecords = useCanDeleteMedicalRecords()

  const openDelete = (target: DeleteMedicalRecordTarget) => {
    setDeleteTarget(target)
    setIsDeleteOpen(true)
  }

  // After a delete the backend has removed that one record and added an audit remark, so re-pull
  // the visit (drops the record from its notes/instructions list) and the remarks list.
  // `getVisitInfo` sends its request when called and dispatches the success action itself; the
  // action it returns only sets the page-wide `loading` flag, which would swap the whole page (and
  // the closing dialog) for a spinner after every delete. So call it without dispatching the result,
  // the same silent refresh PastVisitListener does for prescriptions. The legacy remarks action
  // creator returns `undefined` on its internal catch, so guard before dispatching it.
  const refreshAfterDelete = () => {
    getVisitInfo(visitId)
    const remarksAction = getUpcomingVisitRemarks(visitId)
    if (remarksAction) dispatch(remarksAction)
  }

  const { FirstName, LastName } = patientInfo || {}
  const pageTitle = `${formatNameForPatientChartTitle(FirstName, LastName)} - ${
    isUpcomingVisit ? 'Upcoming Visit Notes' : 'Past Visit Notes'
  }`

  const pageHeader = useAdjustPageHeadEntities({
    title: pageTitle,
  })
  useEffect(
    () => () => {
      document.title = QUICK_MD_TITLE_TEXT
    },
    [],
  )

  useEffect(() => {
    // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
    dispatch(getVisitInfo(visitId))
  }, [])

  // Keyed on the doctor rather than the whole visit, so the silent refresh after a delete doesn't
  // re-fetch the provider (whose action also sets the page-wide `loading` flag).
  useUpdateEffect(() => {
    if (visitInfo.DoctorId) {
      // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
      dispatch(getProviderInfo(visitInfo.DoctorId, visitId))
    }
  }, [visitInfo.DoctorId])

  useEffect(() => {
    if (visitId && patientId)
      postHIPAALogMessage(generateLoggVisitsAccessData({ visitId, patientId, userId: profile?.AdminId || '' }))
  }, [visitId, patientId])

  useUpdateEffect(() => {
    if (visitInfo.PatientId && !patientInfo?.FirstName) {
      // @ts-expect-error TS2554 -- MIGRATION: not approved, fix when encountered
      dispatch(getPatientInfo(patientId ?? visitInfo.PatientId))
    }
  }, [visitInfo])

  useEffect(() => {
    setUserTimeZone(parseTimeZone(profile.TimeZone))
  }, [profile])
  // @ts-expect-error TS6133 -- MIGRATION: not approved, fix when encountered
  const crumbsConfig = useMemo(
    () => [
      {
        site: PATIENT_CHARTS_TEXT,
        location: `${PATIENT_CHARTS_PAGE_ROUTE}${locationState?.search ?? ''}`,
      },
      {
        site: `${patientInfo?.FirstName} ${patientInfo.LastName}`,
        location: `${PATIENT_CHART_PAGE_ROUTE}?id=${patientId}`,
        state: { search: locationState?.search },
      },
      {
        site: VISIT_NOTE_TEXT,
        location: '',
      },
    ],
    [locationState?.search, patientId, patientInfo?.FirstName, patientInfo.LastName],
  )

  useEffect(() => {
    if (error) {
      setTimeout(() => {
        dispatch(setError('', visitId))
      }, 3500)
    }
  }, [dispatch, error])

  const onClickClose = () => {
    navigate(`${PATIENT_CHART_PAGE_ROUTE}?id=${patientId}`)
  }

  if (loading) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <PageLoadingSpinner />
      </div>
    )
  }

  return (
    <div className='past-visit-info-main-wrapper'>
      {prescription ?
        <PrescriptionPopUp
          prescription={prescription}
          onClose={() => {
            setPrescription(null)
          }}
        />
      : null}
      {pageHeader}
      {error ?
        <ErrorAlert styleContainer={errorToastContainerStyle} errorText={error} errorImg={errorIcon} imgAlt='Error' />
      : null}
      <PastVisitListener visitId={visitId} />
      <PastVisitInfoHeader clickClose={onClickClose} visitTime={visitInfo.FromDateTime} userTimeZone={userTimeZone} />
      <div className='past-visit-info-body'>
        <PatientInfoCard
          patient={patientInfo}
          isUpcomingVisit={isUpcomingVisit}
          visitId={visitId}
          provider={provider}
          visitDateTime={visitInfo.FromDateTime}
          method={visitInfo.Method}
          status={getVisitStatus(visitInfo.Status) || ''}
          visitType={visitInfo.VisitTypeName}
          userTimeZone={userTimeZone || Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone}
          patientTimeZone={visitInfo?.TimeZone || Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone}
          hasMatMoud={visitInfo.IsMedicationAssistedTreatment}
        />
        <AddVisitRemarksSection
          visitId={visitId}
          userTimeZone={userTimeZone || Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone}
        />
        <div className='past-visit-info-note-block'>
          <div className='past-visit-info-note-block-title'>{VISIT_NOTE_TEXT}</div>
          <NotesSection
            title={VISIT_NOTE_BLOCK_NOTE_SECTION_TITLE}
            notesList={visitInfo.Notes}
            emptyText={NO_NOTES_TEXT}
            userTimeZone={userTimeZone}
            deleteItem={
              canDeleteMedicalRecords ?
                {
                  ariaLabel: DELETE_NOTE_ARIA_LABEL_TEXT,
                  onDelete: (medicalRecordId, summary) => openDelete({ kind: 'note', medicalRecordId, summary }),
                }
              : undefined
            }
          />
          <PrescriptionsSection
            visitId={visitId}
            onSetPrescription={setPrescription}
            isUpcomingVisit={isUpcomingVisit}
          />

          <ICDCodesSection visitICDCodes={visitInfo.IcdCodes} />
          <NotesSection
            className='patient-past-visit-note-block-patient-instructions'
            title={PATIENT_INSTRUCTIONS_TITLE_TEXT}
            description={VISIBLE_TO_PATIENT_IN_VISIT_SUMMARY_TEXT}
            notesList={visitInfo.PatientInstructions}
            emptyText={NO_PATIENT_INSTRUCTIONS_TEXT}
            userTimeZone={userTimeZone}
            isWithoutSeparator
            deleteItem={
              canDeleteMedicalRecords ?
                {
                  ariaLabel: DELETE_PATIENT_INSTRUCTION_ARIA_LABEL_TEXT,
                  onDelete: (medicalRecordId, summary) =>
                    openDelete({ kind: 'patient-instruction', medicalRecordId, summary }),
                }
              : undefined
            }
          />
        </div>
      </div>
      {/* Kept mounted (DS Popup requirement) — toggle isOpen, don't conditionally render. */}
      <DeleteMedicalRecordModal
        isOpen={isDeleteOpen}
        target={deleteTarget}
        visitId={visitId}
        onClose={() => setIsDeleteOpen(false)}
        onDeleted={refreshAfterDelete}
      />
    </div>
  )
})
